import { MAX_UPLOAD_IMAGE_SIZE_LABEL } from "@/constants/dataset";
import { LLM_ERROR_REASON } from "@/constants/errorCode";

export const ERROR_MESSAGE = {
  SOMETHING_WENT_WRONG: "Something went wrong",
  SESSION_TIMEOUT: "Session Timeout!",
  SOMETHING_WENT_WRONG_DESCRIPTION:
    "Sorry, we’re experiencing an issue while processing your request. Please try again."
};

export const ADVANCED_NORMALIZATION_ERROR_MESSAGES = {
  STD_DEV_REQUIRED: "Standard deviation is required when mean value is filled.",
  MEAN_REQUIRED: "Mean value is required when Stdv value is filled.",
  BOTH_REQUIRED: "Fill in both Mean value and Standard deviation",
  STD_DEV_GREATER_THAN_ZERO: "Standard deviation must be greater than 0",
  VALID_POSITIVE_NUMBER: "Enter a valid positive numeric value."
} as const;

export const MESSAGE = {
  CANNOT_FIND_PLACE: "Cannot find place",
  CONFIRMATION_LEAVE:
    "Are you sure you want to leave? Any unsaved changes will be lost.",
  CONFIRMATION_RESET:
    "This will remove all datasets and settings. You’ll have to start a new analysis to continue.",
  SAVING_YOUR_DATASET: "Please wait while your dataset is being saved.",
  SAVED_YOUR_DATASET_FAILED:
    "Sorry, we couldn’t save your dataset. Please try again.",
  OPEN_PROJECT_SUCCESS: "Configuration successfully loaded.",
  SAVING_NEW_PROJECT: "Please wait while your configuration is being saved.",
  LOADING_CONFIGURATION:
    "Please wait while your configuration is being loaded.",
  LOADING_CONFIGURATION_FAILED:
    "Sorry, we couldn’t load your configuration. Please try again.",
  LOADING_SPATIAL_ANALYSIS: "Spatial Analysis in Progress",
  PREPARE_TABULAR_DATA_AND_MAPVIEW:
    "Preparing the tabular data and map view for your spatial analysis. This may take some time.",
  LOADING_SPATIAL_ANALYSIS_FAILED:
    "Sorry, we couldn’t load the spatial analysis. Please try again.",
  LOADING_ADMIN_AREA_DATA: "Loading administrative area data...",
  DELETE_DATASET_FAILED:
    "Sorry, we couldn't delete the dataset. Please try again.",
  LOADING: "Loading...",
  DATASET_MANAGEMENT_UNPUBLISH_DATASET_CONFIRMATION_DESCRIPTION:
    "This dataset might be used in one or more analyses. Unpublishing it may impact those analyses. You can still edit the dataset after unpublishing.",
  DATASET_MANAGEMENT_PUBLISH_DATASET_CONFIRMATION_DESCRIPTION:
    "Once published, datasets will be available for spatial analysis.",
  DATASET_REPLACE_ALL_SUCCESS: "All data has been successfully replaced.",
  DATASET_ADD_MORE_SUCCESS: "Data has been successfully added.",
  DATASET_DELETE_SUCCESS: "Dataset has been deleted permanently.",
  DATASET_ADD_LOADING: "Please wait while dataset is being loaded.",
  DATASET_ADD_TITLE_SUCCESS: "Dataset Loaded Successfully",
  DATASET_ADD_DESCRIPTION_PREFIX: "The dataset",
  DATASET_ADD_DESCRIPTION_SUCCESS:
    "is now ready for analysis and use on the platform.",
  DATASET_ADD_TITLE_FAILED: "Dataset Failed to Load",
  DATASET_ADD_DESCRIPTION_FAILED:
    "failed to load. Please check the dataset and try again.",
  DATASET_ADD_DESCRIPTION_UNSUPPORTED_TITLE: "Unsupported coordinate system.",
  DATASET_ADD_DESCRIPTION_UNSUPPORTED_CRS:
    "contains invalid coordinate data and can't be used. Please fix the coordinate data and upload the dataset again.",
  DATASET_SPATIAL_AGGREGATION_UPDATED:
    "Spatial aggregation settings were updated. refresh to see the changes.",
  DATASET_STYLE_LOCKED_UNTIL_REFRESH:
    "Styling is locked until you refresh this dataset.",
  DATASET_ADM_AREA_MAPPING_UPDATED:
    "Adm. Area mapping has changed and affected your saved filter",
  DATASET_DELETE_FAILED:
    "Sorry, we couldn’t delete the dataset. Please try again.",
  SAVE_DATA_SETTING_SUCCESS:
    "Country and Spatial Aggregation successfully added.",
  GEOMETRIC_VALUE_ADDED_SUCCESS:
    "Geometric value has been added as a new attribute.",
  CONFIRM_LOG_OUT: "You will need to sign in again to access your account.",
  UNPUBLISH_DATASET_SUCCESS: "Your dataset has been unpublished.",
  PUBLISH_DATASET_SUCCESS: "Your dataset has been published.",
  UNPUBLISH_DATASET_FAILED:
    "Sorry, we couldn’t unpublish the dataset. Please try again.",
  PUBLISH_DATASET_FAILED:
    "Sorry, we couldn’t publish the dataset. Please try again.",
  SETTING_ADD_USER_EMAIL_INVALID:
    "The email address you entered is invalid. Please check the format and try again.",
  SETTING_ADD_USER_EMAIL_ALREADY_EXISTS:
    "This email belongs to an existing user. Use a different email to continue.",
  SETTING_SAVE_USER_TITLE_SUCCESS: "User Successfully Created",
  SETTING_SAVE_USER_DESCRIPTION_SUCCESS: "User has been created successfully.",
  SETTING_SAVE_USER_TITLE_FAILED: "Something Went Wrong",
  SETTING_SAVE_USER_DESCRIPTION_FAILED:
    "We're experiencing an issue processing your request. Please try again.",
  SETTING_SAVE_USER_TITLE_CLOSE: "Unsaved Changes",
  SETTING_SAVE_USER_DESCRIPTION_CLOSE:
    "Are you sure you want to leave? Any unsaved changes will be lost.",
  ANALYSIS_SAVE_AS_USER_DATASET_SUCCESS: "Dataset has been successfully saved.",
  ANALYSIS_SAVE_AS_USER_DATASET_SUCCESS_DESCRIPTION:
    "You can check your datasets by clicking the link below",
  ANALYSIS_SAVE_AS_USER_DATASET_FAILED_TITLE: "Save Failed",
  DATA_SETTING_SAVED: "Data Setting Saved",
  ADM_AREA_MAPPING_SUCCESS: "Mapping Updated Successfully",
  ADM_AREA_MAPPING_SUCCESS_DESCRIPTION:
    "This dataset has been updated and can use Adm Area Filter and is eligible for global filter sync.",
  ADM_AREA_MAPPING_UPDATE_SUCCESS: "Adm Area Mapping Update",
  ADM_AREA_MAPPING_UPDATE_SUCCESS_DESCRIPTION:
    "The Admin Area mapping has been successfully updated.",
  DELETE_GROUP_MANAGEMENT_TITLE_SUCCESS_CONFIRMATION: "Delete This Group?",
  DELETE_GROUP_MANAGEMENT_DESCRIPTION_CONFIRMATION_SUFFIX:
    "will permanently remove all associated data. This action cannot be undone.",
  DELETE_GROUP_MANAGEMENT_DESCRIPTION_CONFIRMATION_PREFIX: "Deleting",
  DELETE_GROUP_MANAGEMENT_TITLE_SUCCESS: "Group Deleted Successfully",
  DELETE_GROUP_MANAGEMENT_DESCRIPTION_SUCCESS:
    "You've successfully removed the group.",
  DELETE_GROUP_MANAGEMENT_TITLE_FAILED: "Group Deletion Failed",
  DELETE_GROUP_MANAGEMENT_DESCRIPTION_FAILED:
    "Sorry, something went wrong. Please remove all users bound to this group and try again.",
  SETTING_WEIGHT_VALUES_INVALID: "At least one weight must be greater than 0.",
  ADD_GROUP_PANEL_TITLE_SUCCESS: "Group Successfully Created",
  ADD_GROUP_PANEL_DESCRIPTION_SUCCESS:
    "You can now assign users and manage this group.",
  ADD_GROUP_PANEL_TITLE_DUPLICATE_ERROR: "Group Name Taken",
  ADD_GROUP_PANEL_DESCRIPTION_PREFIX_DUPLICATE_ERROR: "The name",
  ADD_GROUP_PANEL_DESCRIPTION_SUFFIX_DUPLICATE_ERROR:
    "is already in use. Please enter a different name for this group.",
  EDIT_GROUP_PANEL_TITLE_SUCCESS: "Group Updated",
  EDIT_GROUP_PANEL_DESCRIPTION_SUCCESS:
    "We've updated the information and permissions.",
  EDIT_GROUP_PANEL_TITLE_FAILED: "Something Went Wrong",
  EDIT_GROUP_PANEL_DESCRIPTION_FAILED:
    "Sorry, we're experiencing an issue processing your request. Please try again.",
  CATCHMENT_CONTOUR_INVALID:
    "Can’t generate catchment area. Please try another value.",
  CATCHMENT_CONTOUR_INVALID_DURATION:
    "Duration cannot be more than 60 minutes.",
  CATCHMENT_CONTOUR_INVALID_DISTANCE:
    "Distance cannot be more than 100,000 meters (100 km).",
  CATCHMENT_ROAD_ACCESS_OVER_LIMIT_INFO: (limit: number) =>
    `Road Access is unavailable for more than ${limit.toLocaleString("en-US")} locations.\nReduce the number of locations to continue.`,
  CATCHMENT_ROAD_ACCESS_OVER_LIMIT_CAUTION: (limit: number) => {
    const formatted = limit.toLocaleString("en-US");
    return `Road Access is unavailable for more than ${formatted} locations.\nReduce the number of locations to ≤ ${formatted} to continue.`;
  },
  ANALYSIS_SUMMARY: {
    GENERATING_SUMMARY: "Generating summary, please wait...",
    GENERATING_SUMMARY_FAILED:
      "Sorry, something went wrong while processing the summary. Please try again.",
    NO_DATA_ANALYSIS_SUMMARY_DATASET:
      "No data available to display. Please check your data or try adjusting the configuration menu.",
    GENERATING_SUMMARY_ERROR_WITH_RETRY_TITLE: "Unable to Load Summary",
    GENERATING_SUMMARY_ERROR_WITH_RETRY_DESCRIPTION:
      "Something interrupted the loading process. Your analysis is safe"
  },
  COULD_NOT_FIND_LOCATION: "Sorry, we couldn’t find the location.",
  DATASET_UPLOAD_SUCCESS_TITLE: "Dataset Uploaded Successfully",
  DATASET_UPLOAD_SUCCESS_DESCRIPTION:
    "Your data is now available for analysis.",
  DATASET_UPLOAD_SUCCESS_WITH_WARNING_TITLE:
    "Upload Successful (with Warnings)",
  DATASET_UPLOAD_ERROR_TITLE: "Failed to Add Data",
  DATASET_UPLOAD_ERROR_DESCRIPTION:
    "All data in your dataset already exists. Please review your dataset and try again.",
  DATASET_UPLOAD_ADD_MORE_DUPLICATE_DESCRIPTION:
    "Your data is added and ready for analysis. Some data in your dataset are duplicates of existing data.",
  LEAVE_ANALYSIS_CONFIRMATION_TITLE: "Leave Analysis Summary?",
  LEAVE_ANALYSIS_CONFIRMATION_DESCRIPTION:
    "Are you sure you want to leave? If you continue, your current view will be cleared.",
  UNSAVED_CHANGES_TITLE: "Unsaved Changes",
  ANALYSIS_SUMMARY_DATA_BAR_CHART_LIMIT_10:
    "Only the top 10 data entries are displayed.",
  ANALYSIS_SUMMARY_SUMMARY_DATA_TABLE_LIMIT_INFO_10:
    "This table displays up to 10 entries only.",
  DATASET_UPLOAD_ERROR_FILE_TOO_LARGE_TITLE: "File Too Large",
  DATASET_UPLOAD_ERROR_FILE_TOO_LARGE_DESCRIPTION:
    "The file you tried to upload exceeds the 100MB limit. Please upload a smaller file.",
  DATASET_UPLOAD_ERROR_FILE_NOT_SUPPORTED_TITLE: "Unsupported File Format",
  DATASET_UPLOAD_ERROR_FILE_NOT_SUPPORTED_DESCRIPTION: (
    fileTypesText: string
  ) =>
    `Only ${fileTypesText} files are supported. Please upload one of these formats.`,
  NO_INTERNET_CONNECTION_TITLE: "No Internet Connection",
  NO_INTERNET_CONNECTION_DESCRIPTION:
    "Please check your connection and try again.",
  CHANGE_PASSWORD_SUCCESS_TITLE: "Password Changed Successfully",
  CHANGE_PASSWORD_SUCCESS_DESCRIPTION:
    "You're logged in and ready to continue.",
  CHANGE_PASSWORD_INCORRECT_PASSWORD_TITLE: "Incorrect Password",
  CHANGE_PASSWORD_INCORRECT_PASSWORD_DESCRIPTION:
    "The current password you entered is wrong. Please check and try again.",
  CHANGE_PASSWORD_GENERAL_ERROR_TITLE: "Something Went Wrong",
  CHANGE_PASSWORD_GENERAL_ERROR_DESCRIPTION:
    "Sorry, we're experiencing an issue processing your request. Please try again.",
  AGGREGATION_INFO_TOOLTIP:
    "Data is averaged to reflect general mobility patterns based on date and time range.",
  LOADING_GENERAL: "Please wait while we prepare your request.",
  SUCCESS_GENERAL: "Your request was successful.",
  ERROR_GENERAL: "Something went wrong. Please try again.",
  NO_GEOM_ADMIN_AREA:
    "Please select one attribute to get the value of the Administrative Area.",
  NO_GEOM_POINT_GEOCODING:
    "Please select one attribute for geocoding to get the value of the Point.",
  CANCEL_INPROGRESS_ANALYSIS_CONFIRMATION:
    "Are you sure you want to leave? If you do, the current analysis will stop immediately.",
  CANCEL_INWAITING_ANALYSIS_CONFIRMATION:
    "Are you sure you want to cancel this analysis? If you continue, it will be removed from the queue.",
  ANALYSIS_EXPIRED:
    "This analysis has expired. You need to regenerate it to view the report.",
  SETTING_DELETE_USER_TITLE_SUCCESS: "User Deleted Successfully",
  SETTING_DELETE_USER_DESCRIPTION_SUCCESS:
    "We have removed all associated data.",
  SETTING_DELETE_USER_TITLE_FAILED: "Something Went Wrong",
  SETTING_DELETE_USER_DESCRIPTION_FAILED:
    "Sorry, we're experiencing an issue processing your request. Please try again.",
  MULTIPLE_SEARCH_WITH_SELECT_INFO:
    "Add attributes as filters to narrow down your data.",
  MAP_VIEW_ACTION_COLUMN_FAILED:
    "The geometry is invalid and cannot be rendered, please double check the geometry value.",
  DELETE_DATASET_MANAGEMENT_ROWS_CONFIRMATION_TITLE:
    "Are you sure you want to delete?",
  DELETE_DATASET_MANAGEMENT_ROWS_CONFIRMATION_DESCRIPTION:
    "This action can’t be undone and may affect future data uploads.",
  DOWNLOAD_PDF_AI_SUMMARY_NOT_COMPLETE:
    "The AI summary isn't fully loaded yet. You can still download now, but the summary may be missing in the PDF.",
  DOWNLOAD_PDF_CHARTS_LOADING:
    "Please wait — charts are still loading. PDF will be available once all charts are ready.",
  SPATIAL_ANALYSIS_SUCCESS_TITLE: "Spatial Analysis Completed",
  SPATIAL_ANALYSIS_SUCCESS_DESCRIPTION:
    "Your tabular data and map view are ready. You can now explore the results.",
  SPATIAL_ANALYSIS_NO_DATA_AVAILABLE: "No Data Available",
  SPATIAL_ANALYSIS_SCORE_GENERATION_FAILED: "Score Generation Failed",
  ANALYSIS_HISTORY_SITE_PROFILING_NO_DATA:
    "Output area follows the boundaries of your selected area or sites.",
  SESSION_EXPIRED_TITLE: "You’ve Been Logged Out",
  SESSION_EXPIRED_DESCRIPTION:
    "Your current session has ended. Please log in again to continue.",
  DATASET_CONFIRMATION_CHANGE_GLOBAL_FILTER:
    "Would you like to set this dataset’s boundaries as the filter area for all datasets?",
  DATASET_ADMIN_AREA_POI_DESCRIPTION:
    "Set the location where the POIs will appear.",
  DATASET_ENABLED_DEFINE_ANOTHER_AREA_INPUT:
    "Enable to define another area input",
  SPATIAL_ANALYSIS_SCORE_NOT_AVAILABLE_TITLE: "Score Not Available",
  SPATIAL_ANALYSIS_SCORE_NOT_AVAILABLE_DESCRIPTION:
    "Analysis completed, but a normalized score requires comparing multiple values. Since only one area was selected, there is nothing to compare against. Try switching to grid analysis or selecting an area with multiple units.",
  REPORT_SUMMARY_NO_SCORES_AVAILABLE_TITLE: "No scores available to summarize",
  REPORT_SUMMARY_NO_SCORES_AVAILABLE_DESCRIPTION:
    "We couldn't find any score values to analyze. The summary will be generated once you have scores available.",
  AI_SUMMARY_NO_SCORES_AVAILABLE_TITLE: "No scores available to summarize",
  AI_SUMMARY_NO_SCORES_AVAILABLE_DESCRIPTION:
    "The AI Summary generates insights by evaluating how the  area performs against others based on score. To provide a meaningful summary, the system requires a baseline derived from multiple data points.",
  TIMESERIES_PANEL_VIEW_DATA_DESCRIPTION:
    "By default, the timeline shows the aggregated value for all areas. Select a specific area on the map to view its individual timeline.",
  UNLOCK_AI_POPOVER_TITLE: "Unlock AI Analysis",
  UNLOCK_AI_CONTENT_POPOVER_DESCRIPTION:
    "This feature is available for accounts with AI access.",
  UNLOCK_AI_CONTENT_POPOVER_DESCRIPTION_CONTENT:
    "Please contact the Bvarta sales team to activate AI Analysis for your LOKASI Intelligence workspace",
  UNLOCK_AI_SUMMARY_TITLE: "AI Summary is Not Available",
  UNLOCK_AI_SUMMARY_CONTENT:
    "This feature is available for accounts with AI access. Contact Bvarta sales team to activate AI Summary for your workspace",
  UNLOCK_MODULE_NOT_IN_PLAN_CONTENT:
    "This module isn't included in your organization's plan. Contact us to upgrade.",
  UNLOCK_USER_MANAGEMENT_TITLE: "User Management is Not Available",
  UNLOCK_DATASET_MANAGEMENT_TITLE: "Dataset Management is Not Available",
  AI_SUMMARY_ERROR: {
    TITLE: "AI Summary is temporarily unavailable",
    RETRY: "Try Again",
    // Quota is an account/billing problem the user can't retry away, so it
    // keeps its own copy. Every other failure is treated as transient.
    DESCRIPTION_BY_REASON: {
      [LLM_ERROR_REASON.QUOTA_EXCEEDED]:
        "AI Executive Summary is temporarily unavailable due to service quota",
      [LLM_ERROR_REASON.PROVIDER_ERROR]:
        "We couldn’t generate your summary right now. Please try again in a few minutes.",
      [LLM_ERROR_REASON.UNEXPECTED_ERROR]:
        "We couldn’t generate your summary right now. Please try again in a few minutes."
    } as Record<string, string>,
    DEFAULT_DESCRIPTION:
      "We couldn’t generate your summary right now. Please try again in a few minutes.",
    NEED_HELP: "Need help?",
    CONTACT_SUPPORT: "Contact Support",
    SUPPORT_EMAIL_SUBJECT: "AI Summary is unavailable",
    SUPPORT_MODAL_TITLE: "Contact Support",
    SUPPORT_MODAL_DESCRIPTION:
      "Reach out to our support team and we’ll help you get the AI Summary running again."
  },
  AI_SUMMARY_FEEDBACK: {
    SAVE_FAILED: "Failed to save feedback. Please try again.",
    THANKS_FOR_FEEDBACK: "Thank you for your feedback",
    THANKS_FOR_FEEDBACK_OTHER: "Thanks! Your feedback helps us improve.",
    COPIED_TO_CLIPBOARD: "Copied to clipboard",
    COPY_FAILED: "Failed to copy summary",
    COPY_TOOLTIP: "Copy",
    GOOD_RESPONSE: "Good response",
    BAD_RESPONSE: "Bad response",
    DISLIKE_POPUP: {
      TITLE: "What didn’t you like about this response (optional)?",
      BACK_TO_OPTIONS: "Back to options",
      CLOSE: "Close feedback",
      PLACEHOLDER: "Add specific details",
      SUBMIT: "Submit feedback",
      OPTION_MISLEADING: "Misleading",
      OPTION_INCOMPLETE: "Incomplete",
      OPTION_IRRELEVANT: "Irrelevant",
      OPTION_UNCLEAR: "Unclear"
    }
  },
  DATASET_CONFIRMATION_APPLY_LABEL_FROM_GLOBAL_FILTER: "Yes, apply to all",
  DATASET_CONFIRMATION_KEEP_LABEL_FROM_GLOBAL_FILTER: "No, keep default only",
  DATASET_CONFIRMATION_UPDATE_FROM_GLOBAL_FILTER:
    "You've updated the area filter. Datasets with custom filters won't be affected. Apply changes to all datasets?",
  SHAPEFILE_NOTE:
    "Upload a ZIP or individual files containing all required Shapefile components (.shp, .dbf, and .shx) - all files must share the same base name.",
  SHAPEFILE_ERROR_UNSUPPORTED_MULTIPLE_TITLE: "Cannot Upload Files",
  SHAPEFILE_ERROR_UNSUPPORTED_MULTIPLE_DESCRIPTION:
    "Multiple file upload is only supported for shapefiles",
  SHAPEFILE_ERROR_NOT_SUPPORTED_MULTIPLE_ONETIME_TITLE:
    "Not Support Multiple Shapefile",
  SHAPEFILE_ERROR_NOT_SUPPORTED_MULTIPLE_ONETIME_DESCRIPTION:
    "Multiple Shapefile sets detected. Please upload one set at a time.",
  SHAPEFILE_ERROR_SAMENAME_TITLE: "Files Must Have the Same Name",
  SHAPEFILE_ERROR_SAMENAME_DESCRIPTION:
    "All files must be the same name. Please double check.",
  UPLOAD_DIALOG_DESCRIPTION:
    "Upload your own datasets to be used for analysis or visualization purposes.",
  DATASET_WIDGET_NO_SUPPORTED_DATASET_DESCRIPTION:
    "We did not find any supported datasets for chart.",
  SERVICE_UNAVAILABLE: "Service temporarily unavailable. Please try again.",
  WIDGET_SUCCESS_ADDED_TITLE: "New Widget has been added",
  WIDGET_SUCCESS_ADDED_DESCRIPTION:
    "Your widget has been successfully added to the dashboard.",
  WIDGET_FAILED_TO_PREVIEW_DESCRIPTION:
    "Unable to load preview. Please try again",
  WIDGET_FAILED_TO_ADD_DESCRIPTION: "Unable to save widget. Please try again",
  WIDGET_CONFIGURATION_CONFIRMATION_LEAVE:
    "You have unsaved changes to this widget's configuration. If you close now, your changes will be lost.",
  WIDGET_NO_DATA_PREVIEW_TITLE: "No Data",
  WIDGET_CARD_NO_DATA_AVAILABLE:
    "No data available for the current filter selection. Try adjusting the dataset filters to see results",
  WIDGET_NO_DATA_DEFAULT_TITLE: "Build your visualization",
  WIDGET_NO_DATA_DEFAULT_DESCRIPTION:
    "Configure your chart by selecting a data source",
  WIDGET_CARD_UNAVAILABLE_DATA_SOURCE_TITLE: "Widget Cannot Display Data",
  WIDGET_CARD_UNAVAILABLE_DATA_SOURCE_DESCRIPTION:
    "The dataset linked to this widget is no longer available. You can remove this widget or assign a new data source.",
  WIDGET_CARD_ERROR_TO_RETRY_TITLE: "Unable to Load Widget",
  WIDGET_CARD_ERROR_TO_RETRY_DESCRIPTION:
    "This widget couldn't be loaded. Something interrupted the loading process. The rest of your summary is unaffected — please try again.",
  WIDGET_CARD_STRUCTURE_CHANGE_DESCRIPTION:
    "The dataset structure has changed. Please review the widget configuration",
  WIDGET_REMOVE_CONFIRMATION_TITLE: "Remove Widget?",
  WIDGET_REMOVE_CONFIRMATION_MESSAGE:
    "Are you sure you want to remove this widget? This action cannot be undone.",
  WIDGET_REMOVE_CONFIRM_LABEL: "Remove",
  WIDGET_REMOVE_ERROR_MESSAGE: "Failed to remove widget. Please try again.",
  LOADING_PAGE_TITLE: "Loading your map...",
  LOADING_PAGE_DESCRIPTION:
    "Please wait, you'll be redirected shortly to begin your analysis.",
  LIMIT_THRESHOLD_WARNING: {
    TITLE: "Limit Threshold Detected",
    TITLE_CATCHMENT: "Reach Limit",
    MESSAGE: (
      limit: number,
      variant: "dataset" | "filter-area" | "catchment-dataset"
    ) => {
      const formattedLimit = limit.toLocaleString("en-US");
      if (variant === "filter-area") {
        return `Your selected data is at or near the ${formattedLimit} row limit. Please reduce your area selection to continue.`;
      }
      if (variant === "catchment-dataset") {
        return `Your data has ${formattedLimit} rows and exceeds the maximum limit. Please filter the attributes first before saving the catchment.`;
      }
      return `Your selected data is at or near the ${formattedLimit} row limit. To continue, you can either narrow your area selection or add more attribute filters or do both.`;
    }
  },
  DATA_WEIGHT_AUTO_ADJUSTED: {
    SIMPLE:
      "Dataset changed. Weights were automatically adjusted. Review your settings.",
    ADVANCED:
      "Dataset settings changed; weights were auto-adjusted. Please verify your Data Weight settings to ensure they align with your analysis goals."
  },
  PRODUCT_OVERVIEW: {
    TITLE: "Product Overview",
    DESCRIPTION: "Here is a quick checklist of things to try",
    CLOSE: "Close",
    VIDEOS_TITLE: "Videos",
    SEE_MORE: "See More",
    LOAD_FAILED: "Couldn't load onboarding. Please try again.",
    EMPTY: "No onboarding available yet."
  },
  FIRST_LOGIN_ONBOARDING: {
    CONTINUE_ARIA_LABEL: "Continue to workspace tour",
    TITLE: "Welcome to LOKASI",
    DESCRIPTION: "Welcome to Lokasi Intelligence!",
    BODY: "Lokasi Intelligence helps you analyze locations, understand your market, and make smarter decisions all in one platform. Watch the video above to see how it works."
  },
  COVERAGE_TYPE: {
    PLACEHOLDER_TYPE_RADIUS: "Enter radius value in meters.",
    PLACEHOLDER_TYPE_DISTANCE: "Enter distance value in meters",
    PLACEHOLDER_TYPE_DURATION: "Enter duration value in minutes",
    STANDARD_DESCRIPTION: "Aggregates data within the fixed grid cell boundary",
    CATCHMENT_DESCRIPTION:
      "Aggregates data within a defined catchment from this grid cell center"
  },
  OUTPUT_ANALYSIS_DESCRIPTION: "Select your preferred output visualization.",
  SCORING_OPTION: {
    NEGATIVE_IMPACT_TITLE: "Negative impact",
    NEGATIVE_IMPACT_DESCRIPTION:
      "The aggregation of amounts can lead to inaccurate results if there is data duplication."
  },
  LEGEND_EDIT_CATEGORY: "Edit Category",
  LEGEND_RENAME_CATEGORY: "Rename this category if needed",
  LEGEND_ASSIGNED_VALUES: "Assigned Values",
  LEGEND_ONE_MORE_VALUES:
    "Select one or more values to assign to this category",
  LEGEND_RESET: "Reset",
  LEGEND_NO_VALUES_FOUND_TITLE: "Options Not Found",
  LEGEND_NO_VALUES_FOUND_DESCRIPTION:
    "Please try again with different filters or keywords.",
  LEGEND_SAVE: "Save",
  KML_UPLOAD_SUCCESS_TITLE: "Dataset Uploaded Successfully",
  KML_UPLOAD_SUCCESS_DESCRIPTION:
    "Upload successful, Your KML file has been validated and is ready for visualization.",
  DASIMETRY: {
    ANALYSIS_CONFIGURATION_TITLE: "Analysis Configuration",
    ANALYSIS_CONFIGURATION_DESCRIPTION:
      "Optional Settings to refine analysis behavior",
    PROPORTION_TITLE: "Dasymetric Proportions",
    PROPORTION_DESCRIPTION:
      "Distribute population proportionally based on building ratio per grid. Reduces over/under-estimation in low-density areas.",
    APPLIES_TO_ADMIN_AREA_ONLY: "Only applies to administrative area dataset"
  },
  CUSTOM_RANGE_INFO_LABEL:
    "This label will be shown in the map legend; if empty the range value will be used.",
  CUSTOM_RANGE_INFO_TOOLTIP:
    "Ranges exclude the start value but include the end value (e.g., 50–75 means > 50 and ≤ 75).",
  IMAGE_UPLOAD_TOO_LARGE: `Image must be ${MAX_UPLOAD_IMAGE_SIZE_LABEL} or smaller.`,
  IMAGE_UPLOAD_SUPPORTED_TYPES: "Only SVG, PNG, and JPG images are supported.",
  IMAGE_UPLOAD_SUPPORTED_TYPES_HINT: "Only support .svg, .png and .jpg",
  IMAGE_UPLOAD_INFO: `Recommended resolution 150×150 and max ${MAX_UPLOAD_IMAGE_SIZE_LABEL}`,
  CUSTOM_LEGEND_UPLOAD_ERROR_SAVE_CONFIG:
    "Failed to save project. Image upload failed. Please try saving again.",
  CUSTOM_LEGEND_UPLOAD_ERROR_RUN_ANALYSIS:
    "Failed to run analysis. Image upload failed. Please try running the analysis again.",
  CUSTOM_LEGEND_SAVE_LEGEND_CONFIG_FAILED_AFTER_ANALYSIS:
    "Failed to save legend configuration. Please try saving again."
};

export const EMPTY_STATE_ANALYSIS_HISTORY = {
  LIST: {
    SEARCH_NOT_FOUND: {
      title: "No Results Found",
      desc: "Sorry, we couldn’t find any matches from your keyword. Try using different keyword."
    },
    FILTER_NOT_FOUND: {
      title: "No Results Found",
      desc: "Sorry, no results match your selected filters. Try adjusting the filter options."
    },
    EMPTY_LIST: {
      title: "No Historical Analysis Found",
      desc: "Your past analyses will appear here once you’ve generated one."
    }
  },

  DETAIL: {
    HAVE_LIST_NOT_SELECTED: {
      title: "Select an Analysis to View Details",
      desc: "Select an analysis from the list to view its detailed parameters configuration."
    },
    EMPTY_LIST: {
      title: "No Analysis Details Available",
      desc: "Select an analysis from the list to view its detailed parameters configuration."
    }
  }
};

export const ADD_DATASET_MESSAGE = {
  TITLE: "Add Dataset",
  DESCRIPTION: "Choose how you want to add data to Dataset Management.",
  TARGETIN_DESCRIPTION:
    "Import all available datasets from the connected Targetin account.",
  DURATION_HINT: "This may take a few minutes depending on the amount of data.",
  CONNECTED: "Connected",
  CANCEL: "Cancel",
  SUBMIT: "Import datasets"
};

export const TARGETIN_IMPORT_MESSAGE = {
  SUCCESS_TITLE: "Import Completed Successfully",
  FAILED_TITLE: "Import Unsuccessfully",
  SUCCESS_DESCRIPTION: (datasetName: string) =>
    `${datasetName} have been imported from Targetin.`,
  FAILED_DESCRIPTION: (datasetName: string) =>
    `${datasetName} could not be imported from Targetin.`,
  VIEW: "View"
};

/**
 * One dataset pulled again from Targetin. Separate from the import wording: sync
 * runs on a dataset the user already has, so nothing new "arrives".
 */
export const TARGETIN_SYNC_MESSAGE = {
  LOADING_TITLE: "Database Sync in Progress",
  LOADING_DESCRIPTION:
    "Updating dataset details. This row will automatically refresh once the synchronization is complete.",
  DISMISS: "Dismiss",
  SUCCESS_TITLE: "Dataset synced successfully",
  SUCCESS_DESCRIPTION: (datasetName: string) =>
    `${datasetName} has been successfully synced.`,
  VIEW: "View Now",
  FAILED_TITLE: "Sync failed",
  FAILED_DESCRIPTION: "Unable to sync data. Please try again.",
  /** Shown on the dataset's own page, where the row count is what changed. */
  DETAIL_SUCCESS_TITLE: "Dataset synced successfully",
  DETAIL_SUCCESS_DESCRIPTION: (addedRows: number) =>
    `${addedRows} ${addedRows === 1 ? "row was" : "rows were"} successfully synced to the table.`,
  /** Only the row count is compared, so this says nothing about the contents. */
  DETAIL_NO_NEW_ROWS_TITLE: "Dataset is already up to date",
  DETAIL_NO_NEW_ROWS_DESCRIPTION: "No new or updated rows were found."
};

export const CANCEL_IMPORT_MESSAGE = {
  TITLE: "Cancel Data Import?",
  DESCRIPTION:
    "Are you sure you want to stop importing this dataset? Any progress made so far will be lost.",
  KEEP: "Keep Importing",
  CONFIRM: "Cancel Import"
};

export const DATA_SETTINGS_MESSAGE = {
  TITLE: "Data Settings",
  DESCRIPTION:
    "Manage spatial configuration and Administrative Area Mapping independently.",
  /** Read by screen readers only; the visible description is DESCRIPTION. */
  DIALOG_DESCRIPTION:
    "Data Settings are used to configure the data for the dataset.",
  SPATIAL_TITLE: "Spatial Settings",
  SPATIAL_CARD_DESCRIPTION:
    "Controls how location data is represented and aggregated on the map.",
  SPATIAL_COORDINATE_REJECTED:
    "We couldn't generate the data from attribute selection. Please try again",
  SPATIAL_NO_ATTRIBUTES_NOTE:
    "No attributes available for the selected Spatial Aggregation. Please select another one",
  SPATIAL_ADM_AREA_CODE_NOTE:
    "The selected Administrative Area attribute must contain Administrative Area codes in integer format.",
  ADM_AREA_TITLE: "Adm. Area Mapping",
  ADM_AREA_TITLE_SUFFIX: "(Optional)",
  ADM_AREA_BADGE_LABEL: "Adm. Area Mapping",
  ADM_AREA_CARD_DESCRIPTION:
    "Map your attributes to Administrative Areas to enable Administrative Area filtering and synchronization.",
  ADM_AREA_ALREADY_MAPPED_NOTE:
    "Since your Spatial Aggregation is set to Administrative Area, location mapping has been applied automatically.",
  ADM_AREA_REQUIRES_SPATIAL_NOTE:
    "Requires Country and Spatial Aggregation to be set first",
  ADM_AREA_DESCRIPTION:
    "Map an attribute containing Administrative Area codes to enable Administrative Area Filter and global filter sync.",
  ADM_AREA_ATTRIBUTE_NOTE:
    "Select an attribute containing Administrative Area codes to enable the Administrative Area Filter and synchronize it with the global filter for this dataset.",
  ADM_AREA_ATTRIBUTE_EDIT_NOTE:
    "Filters using Administrative levels that are no longer available after this update will be removed. Other existing filters will remain unchanged.",
  ADM_AREA_EDIT_NOTE_TITLE: "This change may affect existing filters",
  ADM_AREA_VALIDATING_NOTE: "Checking attribute values the area references",
  ADM_AREA_PARTIAL_TITLE: "Some Codes did not Match",
  ADM_AREA_PARTIAL_DESCRIPTION: (matchedRows: number, totalRows: number) =>
    `${matchedRows} of ${totalRows} rows matched to Bvarta Administrative Area reference.`,
  ADM_AREA_PARTIAL_FOOTNOTE:
    "Administrative Area Filter remains available and only shows areas that matched successfully.",
  ADM_AREA_FAILED_TITLE: "Administrative Area Mapping Failed",
  ADM_AREA_FAILED_DESCRIPTION:
    "We couldn't complete the Administrative Area Mapping. Review the selected attribute and try again.",
  ADM_AREA_FAILED_FOOTNOTE:
    "Administrative Area Filter and global filter sync won't be available until the mapping is completed successfully.",
  TIMESERIES_TITLE: "Time Series",
  TIMESERIES_TITLE_SUFFIX: "(Optional)",
  TIMESERIES_CARD_DESCRIPTION:
    "Configure one or more time attributes for time-based analysis and visualization",
  TIMESERIES_BADGE_LABEL: (count: number) =>
    `${count} ${count > 1 ? "Attributes" : "Attribute"}`,
  TIMESERIES_DESCRIPTION:
    "Configure attributes containing time-based data. Each attribute can have its own date format.",
  TIMESERIES_ATTRIBUTES_TITLE: "Time Attribute",
  TIMESERIES_ATTRIBUTES_DESCRIPTION:
    "The attribute will be used for time based analysis and visualization",
  TIMESERIES_ATTRIBUTE_PLACEHOLDER: "Time Attribute",
  TIMESERIES_FORMAT_PLACEHOLDER: "Time Format",
  TIMESERIES_ADD_ATTRIBUTE: "Add Time Attribute",
  TIMESERIES_NUMERIC_REQUIRED_TITLE: "Numeric attribute required",
  TIMESERIES_NUMERIC_REQUIRED_DESCRIPTION:
    "Add at least one numeric attribute to the dataset before configuring Time Series.",
  TIMESERIES_VALID: "Valid time values found.",
  TIMESERIES_CANCEL: "Cancel",
  TIMESERIES_SAVE: "Save",
  TIMESERIES_SAVING: "Saving…",
  TIMESERIES_ERROR_FALLBACK:
    "We couldn't check this attribute. Please try again.",
  TIMESERIES_SAVE_SUCCESS_TITLE: "Data Setting Saved",
  TIMESERIES_SAVE_SUCCESS_DESCRIPTION: "Time Series configuration saved.",
  TIMESERIES_REMOVE_SUCCESS_DESCRIPTION: "Time Series configuration removed.",
  TIMESERIES_SAVE_FAILED_TITLE: "Time Series Not Saved",
  TIMESERIES_SAVE_FAILED_DESCRIPTION:
    "We couldn't save the Time Series configuration. Please try again."
};
